import { EmptyChartState } from "@/components/common/EmptyChartState";
import type { LaseredDataPoint } from "@/types";
import { Divider, Box, Typography } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useAppTheme } from "@/hooks/useAppTheme";
import { getChartTooltipStyle } from "../utils/chartStyles";

interface LaseredClaimantsChartProps {
  data: LaseredDataPoint[];
}

export const LaseredClaimantsChart = ({ data: rawData }: LaseredClaimantsChartProps) => {
  const { theme } = useAppTheme();

  if (rawData.length === 0) {
    return <EmptyChartState />;
  }

  // Group by carrier_name, summing lasered/not-lasered counts across clients
  const grouped: Record<string, { lasered: number; notLasered: number }> = {};

  for (const d of rawData) {
    if (!grouped[d.carrier_name]) {
      grouped[d.carrier_name] = { lasered: 0, notLasered: 0 };
    }
    grouped[d.carrier_name].lasered += d.lasered_count;
    grouped[d.carrier_name].notLasered += d.not_lasered_count;
  }

  // Compute percentages
  const data = Object.entries(grouped).map(([carrier, counts]) => {
    const total = counts.lasered + counts.notLasered || 1;
    return {
      carrier,
      laseredClaimantsPercent: counts.lasered / total,
      noLaseredClaimantsPercent: counts.notLasered / total,
    };
  });

  if (data.length === 0) {
    return <EmptyChartState />;
  }

  return (
    <Box>
      {/* Custom Legend */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">Lasered Claimants</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 0.5,
            }}
          />
          <Typography variant="caption">No Lasered Claimants</Typography>
        </Box>
      </Box>

      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} layout="vertical" stackOffset="expand" margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
          <YAxis
            type="category"
            dataKey="carrier"
            width={100}
            tick={{ fontSize: 10 }}
            tickLine={false}
            axisLine={false}
          />
          <XAxis
            type="number"
            domain={[0, 1]}
            ticks={[0, 0.25, 0.5, 0.75, 1]}
            tickFormatter={(val) => `${val * 100}%`}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 10 }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload.length) return null;
              const { payload: item } = payload[0];
              return (
                <div
                  style={{
                    ...getChartTooltipStyle(theme),
                    padding: "8px",
                  }}
                >
                  <strong>{item.carrier}</strong>
                  <Divider sx={{ mb: 1, mt: 1 }} />
                  <div>Lasered Claimants: {`${(item.laseredClaimantsPercent * 100).toFixed(1)}%`}</div>
                  <div>Unlasered Claimants: {`${(item.noLaseredClaimantsPercent * 100).toFixed(1)}%`}</div>
                </div>
              );
            }}
          />
          <Bar
            dataKey="laseredClaimantsPercent"
            stackId="a"
            name="Lasered Claimants"
            fill={theme.palette.primary.main}
          />
          <Bar
            dataKey="noLaseredClaimantsPercent"
            stackId="a"
            name="No Lasered Claimants"
            fill={theme.palette.background.default}
            stroke={theme.palette.divider}
            strokeWidth={1}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
